import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { ArrowLeft, BookOpen, ShieldCheck, ChevronRight } from 'lucide-react';
import SupportCTA from '../components/common/SupportCTA';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// How many category names to preview on each tile before truncating.
// Keeps the tile height stable across configurations.
const PREVIEW_LIMIT = 4;

// Fallback taglines shown when a surface has zero categories yet —
// avoids an awkward empty third line on a fresh install.
const FALLBACK_TAGLINE = {
  docs: 'How MARQ works · Accounts · Practice · Billing',
  guides: 'Preparation strategies · Best practices · Exam tips',
};

/**
 * /help — chooser page. Two big tiles split by intent:
 *   - Docs  → how the product works (setup, billing, features)
 *   - Guides → exam prep tips / best practices (grade-aware content)
 *
 * The third line on each tile is fetched live from the corresponding
 * `{surface}-categories` collection so it stays in sync with whatever
 * admins actually configure. Falls back to a static tagline when a
 * surface has no categories.
 */
const HelpHubPage = () => {
  const navigate = useNavigate();
  const [docCats, setDocCats] = useState(null); // null=loading, []=none
  const [guideCats, setGuideCats] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      axios.get(`${API}/pages/docs-categories`).then(r => r.data).catch(() => []),
      axios.get(`${API}/pages/guides-categories`).then(r => r.data).catch(() => []),
    ]).then(([d, g]) => {
      if (!alive) return;
      setDocCats(Array.isArray(d) ? d : []);
      setGuideCats(Array.isArray(g) ? g : []);
    });
    return () => { alive = false; };
  }, []);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        <Link to="/dashboard">
          <Button variant="ghost" className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </Link>

        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" data-testid="help-hub-title">
            How can we help?
          </h1>
          <p className="text-slate-600 mt-2">
            Pick a direction — product docs for how MARQ works, or exam guides for prep tips.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="help-hub-tiles">
          <HubTile
            surface="docs"
            title="Docs"
            description="Learn how MARQ works — accounts, plans, practice sessions, live classes."
            cats={docCats}
            fallbackTagline={FALLBACK_TAGLINE.docs}
            icon={<BookOpen className="w-6 h-6" />}
            accent="teal"
            onClick={() => navigate('/help/docs')}
            ctaLabel="Browse Docs"
          />
          <HubTile
            surface="guides"
            title="Guides"
            description="Exam prep tips from mentors — how to plan, revise, and crack your exams."
            cats={guideCats}
            fallbackTagline={FALLBACK_TAGLINE.guides}
            icon={<ShieldCheck className="w-6 h-6" />}
            accent="emerald"
            onClick={() => navigate('/help/guides')}
            ctaLabel="Browse Guides"
          />
        </div>

        <SupportCTA variant="footer" />
      </div>
    </Layout>
  );
};

/**
 * Single hub tile. Accent-driven so we can keep two subtly different
 * colour identities (teal for Docs, emerald for Guides) without
 * duplicating the whole markup twice.
 */
const HubTile = ({ surface, title, description, cats, fallbackTagline, icon, accent, onClick, ctaLabel }) => {
  const themes = {
    teal: {
      hoverBorder: 'hover:border-[#0A7EA4]',
      iconBg: 'bg-[#0A7EA4]/10 text-[#0A7EA4]',
      hoverText: 'group-hover:text-[#0A7EA4]',
      ctaText: 'text-[#0A7EA4]',
    },
    emerald: {
      hoverBorder: 'hover:border-emerald-600',
      iconBg: 'bg-emerald-50 text-emerald-700',
      hoverText: 'group-hover:text-emerald-700',
      ctaText: 'text-emerald-700',
    },
  };
  const t = themes[accent];
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group text-left rounded-xl border border-slate-200 bg-white ${t.hoverBorder} hover:shadow-md transition p-6 flex flex-col gap-4`}
      data-testid={`help-hub-tile-${surface}`}
    >
      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${t.iconBg}`}>
        {icon}
      </div>
      <div>
        <div className={`text-xl font-bold text-slate-900 ${t.hoverText} transition-colors`}>
          {title}
        </div>
        <p className="text-sm text-slate-600 mt-1">{description}</p>
        <TileTagline cats={cats} fallback={fallbackTagline} />
      </div>
      <span className={`inline-flex items-center gap-1 text-sm font-medium mt-auto ${t.ctaText}`}>
        {ctaLabel} <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
      </span>
    </button>
  );
};

/**
 * Renders the live category preview. Shows a low-contrast placeholder
 * while `cats === null` (loading) so the tile height doesn't jump on
 * load, the real category list once it arrives, or the fallback
 * tagline when the surface has no categories configured.
 */
const TileTagline = ({ cats, fallback }) => {
  // Loading — invisible-but-space-reserving placeholder so layout is stable.
  if (cats === null) {
    return (
      <p className="text-xs text-transparent mt-2 select-none" aria-hidden="true">
        {fallback}
      </p>
    );
  }
  if (cats.length === 0) {
    return <p className="text-xs text-slate-500 mt-2">{fallback}</p>;
  }
  const preview = cats.slice(0, PREVIEW_LIMIT).map(c => c.name);
  const extra = cats.length - preview.length;
  const suffix = extra > 0 ? ` · +${extra} more` : '';
  return (
    <p className="text-xs text-slate-500 mt-2" data-testid="help-tile-tagline">
      {preview.join(' · ')}{suffix}
    </p>
  );
};

export default HelpHubPage;
